import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { IconTruck, IconFuel } from "../components/icons";
import type { TransportFuelDashboardDto } from "@bi/types";

/** km/l e R$/km com 2 casas (fmtNumber arredonda demais p/ esses indicadores). */
const fmt2 = (v: number | undefined) =>
  v === undefined ? "—" : v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function FuelPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading } = useQuery({
    queryKey: ["transport", "fuel", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportFuelDashboardDto>(
        `/dashboards/transport/fuel?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ).catch(() => null),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const p = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. mês anterior";

  // O card principal é o DIESEL, para poder ser conferido contra a Relação de
  // Abastecimentos; a comparação com o período anterior segue o mesmo recorte.
  const costDelta        = computeDelta(s?.dieselCost,      p?.dieselCost,      { direction: "higher_is_worse" });
  const litersDelta      = computeDelta(s?.dieselLiters,    p?.dieselLiters,    { direction: "neutral" });
  const priceDelta       = computeDelta(s?.avgPricePerLiter, p?.avgPricePerLiter, { direction: "higher_is_worse" });
  const consumptionDelta = computeDelta(s?.avgConsumption,  p?.avgConsumption,  { direction: "higher_is_better" });
  const costPerKmDelta   = computeDelta(s?.costPerKm,       p?.costPerKm,       { direction: "higher_is_worse" });
  const kmDelta          = computeDelta(s?.km,              p?.km,              { direction: "higher_is_better" });

  const byDay      = data?.byDay      ?? [];
  const costTrend  = data?.costTrend  ?? [];
  const byFuelType = data?.byFuelType ?? [];
  const topVehicles = data?.topVehicles ?? [];
  const vehicleList = useProgressiveList(topVehicles);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Combustível"
        subtitle="Gasto, consumo, custo por km e ranking de veículos por abastecimento"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPIStatCard
          label="Gasto com Diesel" memory={{ cardKey: "combustivel.gasto-diesel", from, to, expected: s?.dieselCost }}
          value={s ? fmtBRL(s.dieselCost) : "—"}
          // Bate com o Total Geral da "Relação de Abastecimentos" do cliente.
          // O que sobra (arla + gasolina) está dito no subtítulo, não somado.
          subtitle={s && s.otherFuelCost > 0
            ? `+ ${fmtBRL(s.otherFuelCost)} de arla e gasolina`
            : "confere com a Relação de Abastecimentos"}
          delta={costDelta} comparisonLabel={cmp}
          status={s && s.dieselCost > 0 ? "warning" : "neutral"} loading={isLoading}
        />
        <KPIStatCard
          label="Litros de Diesel" memory={{ cardKey: "combustivel.litros-diesel", from, to, expected: s?.dieselLiters }}
          value={s ? fmtNumber(Math.round(s.dieselLiters)) : "—"}
          subtitle={s && s.otherFuelLiters > 0
            ? `+ ${fmtNumber(Math.round(s.otherFuelLiters))} L de arla e gasolina`
            : undefined}
          delta={litersDelta} comparisonLabel={cmp}
          status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Preço Médio / Litro" memory={{ cardKey: "combustivel.preco-medio", from, to, expected: s?.avgPricePerLiter }}
          value={s ? fmtBRL(s.avgPricePerLiter) : "—"}
          subtitle="diesel"
          delta={priceDelta} comparisonLabel={cmp}
          status="neutral" loading={isLoading}
        />
        <KPIStatCard
          label="Consumo Médio (km/l · diesel)" memory={{ cardKey: "combustivel.consumo", from, to, expected: s?.avgConsumption }}
          value={s ? fmt2(s.avgConsumption) : "—"}
          delta={consumptionDelta} comparisonLabel={cmp}
          status={s ? (s.avgConsumption > 0 ? "good" : "neutral") : "neutral"} loading={isLoading}
        />
        <KPIStatCard
          label="Custo por KM" memory={{ cardKey: "combustivel.custo-km", from, to, expected: s?.costPerKm }}
          value={s ? `R$ ${fmt2(s.costPerKm)}` : "—"}
          delta={costPerKmDelta} comparisonLabel={cmp}
          status={s && s.costPerKm > 0 ? "warning" : "neutral"} loading={isLoading}
        />
        <KPIStatCard
          label="KM Rodados" memory={{ cardKey: "combustivel.km", from, to, expected: s?.km }}
          value={s ? fmtNumber(Math.round(s.km)) : "—"}
          // Só o hodômetro do diesel (arla tem cadeia própria, que repete o mesmo
          // trecho) e só o intervalo fisicamente possível. Quando alguma linha é
          // descartada, o card diz — senão o número diverge do relatório sem
          // explicação (ver apps/api/src/shared/fuel-km.ts).
          subtitle={s && s.discardedKmRefuels > 0
            ? `${s.discardedKmRefuels} abastecimento(s) com odômetro furado fora da conta`
            : "hodômetro dos abastecimentos de diesel"}
          delta={kmDelta} comparisonLabel={cmp}
          status="neutral" loading={isLoading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Gasto com combustível por dia" subtitle="Valor — período" height={240}
          isEmpty={!isLoading && byDay.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byDay} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fuel" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v: number, n: string) => [n === "liters" ? `${fmtNumber(v)} L` : fmtBRL(v), n === "liters" ? "Litros" : "Gasto"]}
                labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Area type="monotone" dataKey="cost" stroke="var(--brand)" strokeWidth={2} fill="url(#grad-fuel)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Evolução do gasto" subtitle="Por mês — últimos 5 meses" height={240}
          isEmpty={!isLoading && costTrend.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costTrend} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Gasto"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Bar dataKey="cost" fill="var(--brand)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Ranking veículos + por tipo de combustível */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top vehicles */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconTruck className="w-4 h-4 text-gray-400" />
              Gasto por Veículo
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topVehicles.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : vehicleList.visible.map((v, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{v.plate}</p>
                  <p className="text-xs text-gray-400">
                    {[v.brand, `${fmtNumber(Math.round(v.liters))} L`, v.costPerKm > 0 ? `R$ ${fmt2(v.costPerKm)}/km` : null].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900">{v.formatted}</p>
              </div>
            ))}
            {!isLoading && <LoadMoreBar list={vehicleList} label="veículos" />}
          </div>
        </div>

        {/* By fuel type */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconFuel className="w-4 h-4 text-gray-400" />
              Gasto por Tipo de Combustível
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : byFuelType.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : byFuelType.map((f, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{f.fuelType}</p>
                  <p className="text-xs text-gray-400">{fmtNumber(Math.round(f.liters))} L</p>
                </div>
                <p className="text-sm font-semibold text-tm-700 flex-shrink-0">{f.formatted}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
